import { useRef, useState } from 'react';
import { getErrorMessage } from '@/lib/platform';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

/** A selectable IGM grievance sub-category (descriptor code → friendly label). */
export interface ReportCategory {
  code: string;
  label: string;
}

/** Extra fields the report form may collect alongside the free-text description. */
export interface ReportExtras {
  subCategory?: string;
  contactEmail?: string;
  images?: string[];
}

const MAX_IMAGES = 3;
const ACCEPT = 'image/png,image/jpeg,image/webp';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Shared "report a problem" sheet used by the metro/bus ticket detail and the
 * live-ride screen. The caller's onSubmit raises the IGM issue and routes to the
 * right /support/$category/$issueId detail.
 *
 * The form adapts to what the surface needs: a sub-category picker (when
 * `categories` is given — required so the grievance is classified, not guessed),
 * a required email (metro IGM 1.0.0 mandates a complainant email), and screenshot
 * upload (when an `uploader` is given). Identity (name/phone) is NOT collected —
 * the backend resolves it from the authenticated account.
 */
export function ReportProblemModal({
  onClose,
  onSubmit,
  placeholder = 'Describe the issue…',
  categories,
  requireEmail = false,
  defaultEmail = '',
  uploader,
}: {
  onClose: () => void;
  onSubmit: (description: string, extras: ReportExtras) => Promise<void>;
  placeholder?: string;
  categories?: ReportCategory[];
  requireEmail?: boolean;
  defaultEmail?: string;
  uploader?: (file: File) => Promise<string>;
}) {
  const [description, setDescription] = useState('');
  const [subCategory, setSubCategory] = useState(categories?.[0]?.code ?? '');
  const [email, setEmail] = useState(defaultEmail);
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const emailInvalid = requireEmail && !EMAIL_RE.test(email.trim());
  const categoryMissing = !!categories && categories.length > 0 && !subCategory;
  const canSubmit = !!description.trim() && !categoryMissing && !emailInvalid && !uploading;

  async function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = ''; // allow re-selecting the same file
    if (!uploader || files.length === 0) return;
    setError('');
    setUploading(true);
    try {
      const room = MAX_IMAGES - images.length;
      const next: string[] = [];
      for (const file of files.slice(0, room)) {
        next.push(await uploader(file));
      }
      setImages((cur) => [...cur, ...next]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(description.trim(), {
        subCategory: subCategory || undefined,
        contactEmail: email.trim() || undefined,
        images: images.length ? images : undefined,
      });
    } catch (e) {
      setError(getErrorMessage(e));
      setSubmitting(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Report a problem"
      footer={
        <div className="wb-row wb-gap-8" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={submitting || !canSubmit}>
            {submitting ? 'Sending…' : 'Submit'}
          </Button>
        </div>
      }
    >
      <div className="wb-col wb-gap-12">
        {categories && categories.length > 0 && (
          <fieldset className="wb-col wb-gap-8" style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend className="wb-label" style={{ marginBottom: 2 }}>
              What's wrong?
            </legend>
            {categories.map((c) => (
              <label
                key={c.code}
                className={`wb-option ${subCategory === c.code ? 'is-selected' : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <span className="wb-radio-dot" />
                <input
                  type="radio"
                  name="report-subcategory"
                  value={c.code}
                  checked={subCategory === c.code}
                  onChange={() => setSubCategory(c.code)}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <span className="wb-grow">{c.label}</span>
              </label>
            ))}
          </fieldset>
        )}

        <textarea
          autoFocus
          aria-label="Describe the problem"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className="wb-field wb-field--area"
        />

        {requireEmail && (
          <div>
            <label htmlFor="report-email" className="wb-label">
              Email <span className="wb-ink3">(so the operator can reach you)</span>
            </label>
            <input
              id="report-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="wb-field"
            />
          </div>
        )}

        {uploader && (
          <div className="wb-col wb-gap-8">
            <p className="wb-label" style={{ margin: 0 }}>
              Screenshots <span className="wb-ink3">(optional, up to {MAX_IMAGES})</span>
            </p>
            {images.length > 0 && (
              <div className="wb-row wb-wrap wb-gap-8">
                {images.map((url, i) => (
                  <div key={url} style={{ position: 'relative' }}>
                    <img
                      src={url}
                      alt={`Screenshot ${i + 1}`}
                      style={{
                        height: 64,
                        width: 64,
                        borderRadius: 'var(--r-sm)',
                        border: '1px solid var(--line)',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      type="button"
                      aria-label="Remove screenshot"
                      onClick={() => setImages((cur) => cur.filter((u) => u !== url))}
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        background: 'var(--ink)',
                        color: 'var(--paper)',
                        fontSize: 12,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {images.length < MAX_IMAGES && (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="plus"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Add screenshot'}
                </Button>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              onChange={onPickFiles}
              className="hidden"
            />
          </div>
        )}

        {emailInvalid && email.trim() !== '' && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            Enter a valid email address.
          </p>
        )}
        {error && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}
      </div>
    </Sheet>
  );
}
